import type {
  ApiClient,
  ApiClientOptions,
  ApiClientWithAuth,
  AuthRequest,
  AuthResponse,
  RequestOptions
} from '@/libs/api/base/api.types.ts'
import { useSessionStorage } from '@vueuse/core'
import ky, { type Options as KyClientOptions, type SearchParamsOption } from 'ky'
import { getBasicAuth } from '../utils/basic-auth'
import type { BasicAuth } from '../../../ts/types/basic-auth'

export function createApiClient(options: ApiClientOptions): ApiClient {
  const mergedOptions: KyClientOptions = {
    ...options,
    timeout: options.timeout ?? 60000,
    retry: options.retry ?? 3,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...options.headers
    },
    hooks: {
      beforeRequest: [
        (request, requestOptions) => options.beforeRequestHook?.(request, requestOptions)
      ]
    }
  }

  const client = ky.create(mergedOptions)

  function get<T>(url: string, params?: SearchParamsOption, options?: RequestOptions): Promise<T> {
    return client
      .get(url, {
        ...options,
        searchParams: params
      })
      .json<T>()
  }

  function post<B, T>(
    url: string,
    body: B,
    params?: SearchParamsOption,
    options?: RequestOptions
  ): Promise<T> {
    return client
      .post(url, {
        ...options,
        json: body,
        searchParams: params
      })
      .json<T>()
  }

  function put<B, T>(
    url: string,
    body: B,
    params?: SearchParamsOption,
    options?: RequestOptions
  ): Promise<T> {
    return client
      .put(url, {
        ...options,
        json: body,
        searchParams: params
      })
      .json<T>()
  }

  function deleteFn<B, T>(
    url: string,
    body: B,
    params?: SearchParamsOption,
    options?: RequestOptions
  ): Promise<T> {
    return client
      .delete(url, {
        ...options,
        json: body,
        searchParams: params
      })
      .json<T>()
  }

  function patch<B, T>(
    url: string,
    body: B,
    params?: SearchParamsOption,
    options?: RequestOptions
  ): Promise<T> {
    return client
      .patch(url, {
        ...options,
        json: body,
        searchParams: params
      })
      .json<T>()
  }

  function head<B, T>(
    url: string,
    body: B,
    params?: SearchParamsOption,
    options?: RequestOptions
  ): Promise<T> {
    return client
      .head(url, {
        ...options,
        json: body,
        searchParams: params
      })
      .json<T>()
  }

  return { get, post, put, ['delete']: deleteFn, patch, head }
}

export function createApiClientWithAuth(options: ApiClientOptions): ApiClientWithAuth {
  const { key, auth, overwriteTokenKey } = options
  const accessToken = useSessionStorage<string | null>(getTokenKey(), null)

  function getTokenKey(): string {
    return overwriteTokenKey ?? `${key}_access_token`
  }

  const getIsAuthenticated = () => !!accessToken.value

  const client = createApiClient({
    beforeRequestHook(request, { context }) {
      const bearerToken = accessToken.value
      if (bearerToken) {
        request.headers.set('Authorization', `Bearer ${bearerToken}`)
      } else if (context?.basicAuth) {
        const { username, password } = context.basicAuth as BasicAuth
        request.headers.set('Authorization', `Basic ${btoa(`${username}:${password}`)}`)
      }
    },
    ...options
  })

  async function authenticate(): Promise<AuthResponse> {
    const basicAuth = getBasicAuth()
    const options: RequestOptions = { context: { basicAuth } }
    const response = await client.post<Partial<AuthRequest>, AuthResponse>(
      'oauth/token',
      {
        grant_type: auth?.grant_type ?? 'client_credentials',
        client_id: auth?.client_id,
        client_secret: auth?.client_secret
      },
      undefined,
      { ...options }
    )

    accessToken.value = response.access_token

    return response
  }

  return {
    ...client,
    getIsAuthenticated,
    authenticate
  }
}
